/*
 * DebtorReminders.jsx — iter198
 *
 * Customer-side collection cockpit. Mirror of /creditor-reminders but with
 * extra risk classification (Regular / Watch / Alert) + AI-style strategy
 * bullets driven by the aged distribution + per-client payment history.
 *
 * Sources both GST invoices and Enterprise (non-GST) bills from the
 * sales_orders collection — the backend joins receipt vouchers to compute
 * the real outstanding instead of trusting the (often-stale) paid_amount
 * field.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  AlertTriangle, Calendar, ChevronRight, CheckCircle2, Clock, Flame,
  Mail, MessageCircle, Phone, RefreshCw, ShieldCheck, ShieldAlert,
  ShieldOff, TrendingUp, Users, Wallet, Lightbulb, X,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

const RISK_CHIP = {
  REGULAR: { cls: 'bg-emerald-900/40 text-emerald-300 border-emerald-700/60', Icon: ShieldCheck },
  WATCH:   { cls: 'bg-amber-900/40 text-amber-300 border-amber-700/60',       Icon: ShieldAlert },
  ALERT:   { cls: 'bg-red-900/40 text-red-300 border-red-700/60',             Icon: ShieldOff },
};

export default function DebtorReminders() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showRiskDrawer, setShowRiskDrawer] = useState(null); // single risk client

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/debtor-reminders/dashboard`, { withCredentials: true, bypassCache: true });
      setData(r.data);
    } catch (e) { toast.error('Failed to load reminders'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const shareWhatsApp = async (club) => {
    if (!club.client_phone) { toast.error('No phone number on client record'); return; }
    try {
      const r = await axios.post(`${API}/debtor-reminders/whatsapp-preview`,
        { client: club.client, phone: club.client_phone }, { withCredentials: true });
      if (r.data.wa_url) window.open(r.data.wa_url, '_blank');
      else { navigator.clipboard.writeText(r.data.text); toast.success('Message copied — paste in WhatsApp'); }
    } catch { toast.error('Failed to compose'); }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const bulkMarkReceived = async () => {
    if (selectedIds.size === 0) { toast.error('Select at least one invoice'); return; }
    const ref = window.prompt('Bank reference / UTR / cheque no (optional):') || '';
    const method = window.prompt('Payment method (NEFT / RTGS / UPI / Cheque / Cash):', 'NEFT') || 'NEFT';
    try {
      const r = await axios.post(`${API}/debtor-reminders/mark-paid`, {
        invoice_ids: Array.from(selectedIds), reference: ref, payment_method: method,
      }, { withCredentials: true });
      toast.success(`Marked ${r.data.paid_count} invoice(s) paid · ${fmt(r.data.total_paid)} received`);
      setSelectedIds(new Set());
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  if (loading) return <div className="text-zinc-400 text-sm animate-pulse p-6">Loading collection cockpit…</div>;
  if (!data) return null;

  const totalOpen = data.grand_total_open || 0;
  const overdueShare = totalOpen > 0 ? (data.overdue.total / totalOpen * 100).toFixed(0) : 0;

  return (
    <div className="space-y-5" data-testid="debtor-reminders-page">
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-zinc-50">Debtor Collection Reminders</h1>
          <p className="text-zinc-500 text-sm mt-0.5">
            Overdue + upcoming receivables across <span className="text-emerald-300">GST invoices</span> + <span className="text-amber-300">Enterprise bills</span>. With per-client risk classification.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {selectedIds.size > 0 && (
            <button onClick={bulkMarkReceived}
              data-testid="debtor-bulk-received"
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold rounded-md">
              <CheckCircle2 size={13}/> Mark {selectedIds.size} received
            </button>
          )}
          <button onClick={load} className="p-2 rounded-xl border border-zinc-800 text-zinc-400 hover:bg-zinc-800" data-testid="debtor-refresh">
            <RefreshCw size={14}/>
          </button>
        </div>
      </div>

      {/* ── KPI Strip ───────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="debtor-kpi-strip">
        <Kpi label="Overdue"            value={fmt(data.overdue.total)}     hot count={data.overdue.count}/>
        <Kpi label="Due Today"          value={fmt(data.pay_today.total)}        count={data.pay_today.count}/>
        <Kpi label="Upcoming (7 days)"  value={fmt(data.upcoming_7d.total)}      count={data.upcoming_7d.count}/>
        <Kpi label={`Open Total · ${overdueShare}% overdue`} value={fmt(totalOpen)} />
      </div>

      {/* ── Strategy Bullets + Risk Summary ─────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="lg:col-span-2 bg-zinc-900 border border-zinc-800 rounded-2xl p-4" data-testid="debtor-strategy-card">
          <div className="flex items-center gap-2 mb-2">
            <Lightbulb size={15} className="text-amber-400"/>
            <h3 className="text-zinc-100 font-semibold text-sm">Today&apos;s Collection Strategy</h3>
          </div>
          <ul className="space-y-1.5 text-[12px] text-zinc-300">
            {(data.strategy || []).map((b, i) => (
              <li key={i} className="flex items-start gap-2"><span className="text-amber-400 mt-0.5">›</span>{b}</li>
            ))}
          </ul>
        </div>
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-4" data-testid="debtor-risk-summary-card">
          <div className="flex items-center gap-2 mb-2">
            <Users size={15} className="text-sky-400"/>
            <h3 className="text-zinc-100 font-semibold text-sm">Risk Distribution</h3>
          </div>
          <div className="space-y-2">
            <RiskRow label="REGULAR" count={data.risk_summary.regular_count || 0} />
            <RiskRow label="WATCH"   count={data.risk_summary.watch_count   || 0} />
            <RiskRow label="ALERT"   count={data.risk_summary.alert_count   || 0} />
          </div>
        </div>
      </div>

      {/* ── Per-client risk table ─────────────────────────────────────── */}
      {data.risk_clients?.length > 0 && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden" data-testid="debtor-risk-table">
          <div className="px-4 py-3 border-b border-zinc-800 text-zinc-200 font-semibold text-sm flex items-center gap-2">
            <TrendingUp size={14} className="text-sky-400"/> Per-Client Risk Snapshot
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-zinc-950 text-[10px] uppercase text-zinc-500 tracking-wider">
                <tr>
                  <th className="px-3 py-2 text-left">Client</th>
                  <th className="px-3 py-2 text-left">Risk</th>
                  <th className="px-3 py-2 text-right">Open</th>
                  <th className="px-3 py-2 text-right">0-30d</th>
                  <th className="px-3 py-2 text-right">31-60</th>
                  <th className="px-3 py-2 text-right">61-90</th>
                  <th className="px-3 py-2 text-right">90+</th>
                  <th className="px-3 py-2 text-left">Insight</th>
                </tr>
              </thead>
              <tbody>
                {data.risk_clients.map((r, i) => {
                  const chip = RISK_CHIP[r.risk_level] || RISK_CHIP.REGULAR;
                  const ChipIcon = chip.Icon;
                  return (
                    <tr key={i} onClick={() => setShowRiskDrawer(r)}
                      data-testid={`debtor-risk-row-${i}`}
                      className="border-t border-zinc-800/40 hover:bg-zinc-800/30 cursor-pointer">
                      <td className="px-3 py-2 text-zinc-100">{r.client}</td>
                      <td className="px-3 py-2">
                        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold border ${chip.cls}`}>
                          <ChipIcon size={10}/> {r.risk_level}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-zinc-100">{fmt(r.outstanding_total)}</td>
                      <td className="px-3 py-2 text-right font-mono text-emerald-300">{fmt(r.buckets['0_30'])}</td>
                      <td className="px-3 py-2 text-right font-mono text-amber-300">{fmt(r.buckets['31_60'])}</td>
                      <td className="px-3 py-2 text-right font-mono text-orange-300">{fmt(r.buckets['61_90'])}</td>
                      <td className="px-3 py-2 text-right font-mono text-red-300">{fmt(r.buckets['90_plus'])}</td>
                      <td className="px-3 py-2 text-zinc-400 text-[11px] max-w-[260px] truncate">{r.risk_note}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Bucket sections ────────────────────────────────────────────── */}
      <Bucket title="🔴 Overdue"           data={data.overdue}      tone="red"    selectedIds={selectedIds} toggle={toggleSelect} onShare={shareWhatsApp}/>
      <Bucket title="🟡 Due Today"         data={data.pay_today}    tone="amber"  selectedIds={selectedIds} toggle={toggleSelect} onShare={shareWhatsApp}/>
      <Bucket title="🟢 Upcoming (7 days)" data={data.upcoming_7d}  tone="emerald" selectedIds={selectedIds} toggle={toggleSelect} onShare={shareWhatsApp}/>

      {/* Risk drawer */}
      {showRiskDrawer && (
        <RiskClientDrawer client={showRiskDrawer} onClose={() => setShowRiskDrawer(null)} />
      )}
    </div>
  );
}

function Kpi({ label, value, count, hot }) {
  return (
    <div className={`bg-zinc-900 border rounded-xl p-3 ${hot ? 'border-red-700/50' : 'border-zinc-800'}`} data-testid={`debtor-kpi-${label.toLowerCase().replace(/\s/g,'-')}`}>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div className={`text-base font-bold font-mono mt-0.5 ${hot ? 'text-red-300' : 'text-zinc-100'}`}>{value}</div>
      {count != null && <div className="text-[10px] text-zinc-500 mt-0.5">{count} invoice{count !== 1 ? 's' : ''}</div>}
    </div>
  );
}

function RiskRow({ label, count }) {
  const chip = RISK_CHIP[label] || RISK_CHIP.REGULAR;
  const Icon = chip.Icon;
  return (
    <div className="flex items-center justify-between" data-testid={`debtor-risk-summary-${label.toLowerCase()}`}>
      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold border ${chip.cls}`}>
        <Icon size={10}/> {label}
      </span>
      <span className="font-mono text-zinc-100 font-semibold">{count}</span>
    </div>
  );
}

function Bucket({ title, data, tone, selectedIds, toggle, onShare }) {
  if (!data || data.count === 0) return null;
  const colourCls = {
    red:     'border-red-800/60 bg-red-950/10',
    amber:   'border-amber-800/60 bg-amber-950/10',
    emerald: 'border-emerald-800/60 bg-emerald-950/10',
  }[tone] || 'border-zinc-800 bg-zinc-900';

  return (
    <div className={`border rounded-2xl overflow-hidden ${colourCls}`} data-testid={`debtor-bucket-${tone}`}>
      <div className="px-4 py-3 flex items-center justify-between border-b border-zinc-800/60">
        <h3 className="text-zinc-100 font-semibold text-sm flex items-center gap-2">
          {title}
          <span className="text-[11px] font-normal text-zinc-500">{data.count} invoice{data.count !== 1 ? 's' : ''} · {fmt(data.total)}</span>
        </h3>
      </div>
      <div className="divide-y divide-zinc-800/40">
        {data.clubs.map((club) => (
          <div key={club.client + (club.from_date || '')} className="px-4 py-3" data-testid={`debtor-club-${club.client}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-zinc-100 font-medium text-sm">{club.client}</span>
                  {club.max_days_overdue > 0 && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-900/40 text-red-300 border border-red-700/40">
                      {club.max_days_overdue}d overdue
                    </span>
                  )}
                  {club.urgent && <Flame size={11} className="text-amber-400"/>}
                </div>
                <div className="text-[11px] text-zinc-500 mt-0.5">
                  {club.count} invoice{club.count !== 1 ? 's' : ''} · {club.from_date}{club.to_date && club.to_date !== club.from_date ? ` → ${club.to_date}` : ''}
                </div>
              </div>
              <div className="text-right flex-shrink-0">
                <div className="font-mono text-zinc-100 font-bold text-sm">{fmt(club.total)}</div>
                <div className="flex items-center gap-1.5 mt-1.5">
                  {club.client_phone && (
                    <a href={`tel:${club.client_phone}`} className="p-1 rounded text-emerald-400 hover:bg-zinc-800" title="Call">
                      <Phone size={11}/>
                    </a>
                  )}
                  {club.client_phone && (
                    <button onClick={() => onShare(club)} className="p-1 rounded text-green-400 hover:bg-zinc-800" title="WhatsApp"
                      data-testid={`debtor-wa-${club.client}`}>
                      <MessageCircle size={11}/>
                    </button>
                  )}
                  {club.client_email && (
                    <a href={`mailto:${club.client_email}`} className="p-1 rounded text-blue-400 hover:bg-zinc-800" title="Email">
                      <Mail size={11}/>
                    </a>
                  )}
                </div>
              </div>
            </div>
            <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-1">
              {club.invoices.map(inv => {
                const checked = selectedIds.has(inv.id);
                return (
                  <label key={inv.id} className={`flex items-center justify-between gap-2 px-2 py-1.5 rounded border text-[11px] cursor-pointer transition-colors ${checked ? 'bg-emerald-950/40 border-emerald-700/40' : 'bg-zinc-950/60 border-zinc-800 hover:border-zinc-700'}`}>
                    <div className="flex items-center gap-2 min-w-0">
                      <input type="checkbox" checked={checked} onChange={() => toggle(inv.id)} className="accent-emerald-500"
                        data-testid={`debtor-invoice-check-${inv.id}`}/>
                      <span className="font-mono text-amber-300 truncate">{inv.invoice_number}</span>
                      <span className={`text-[9px] px-1 py-0.5 rounded ${inv.gst_enabled ? 'bg-emerald-900/30 text-emerald-300' : 'bg-amber-900/30 text-amber-300'}`}>
                        {inv.gst_enabled ? 'GST' : 'TE'}
                      </span>
                      <span className="text-zinc-500 text-[10px]">{inv.invoice_date || '—'}</span>
                    </div>
                    <span className="font-mono text-zinc-100 flex-shrink-0">{fmt(inv.outstanding)}</span>
                  </label>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function RiskClientDrawer({ client, onClose }) {
  const buckets = client.buckets || {};
  const chip = RISK_CHIP[client.risk_level] || RISK_CHIP.REGULAR;
  const ChipIcon = chip.Icon;
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={onClose} data-testid="debtor-risk-drawer">
      <div className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
          <div>
            <h3 className="text-zinc-100 text-sm font-bold flex items-center gap-2">
              <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold border ${chip.cls}`}>
                <ChipIcon size={10}/> {client.risk_level}
              </span>
              {client.client}
            </h3>
            <p className="text-zinc-500 text-[11px] mt-0.5">{client.risk_note}</p>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-200" data-testid="debtor-risk-drawer-close">
            <X size={14}/>
          </button>
        </div>
        <div className="p-4 space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <Stat label="Open Invoices"  val={client.open_invoices}/>
            <Stat label="Outstanding"    val={fmt(client.outstanding_total)}/>
            <Stat label="Credit Period"  val={`${client.credit_days}d`}/>
            <Stat label="Avg pays in"    val={client.history?.avg_days_to_pay ? `${client.history.avg_days_to_pay}d` : '—'}/>
          </div>
          <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-2">
            <div className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1.5">Aged Outstanding</div>
            {[
              ['0-30 days',  buckets['0_30'],    'emerald'],
              ['31-60 days', buckets['31_60'],   'amber'],
              ['61-90 days', buckets['61_90'],   'orange'],
              ['90+ days',   buckets['90_plus'], 'red'],
            ].map(([label, val, color]) => (
              <div key={label} className="flex items-center justify-between text-[11px] py-0.5">
                <span className="text-zinc-400">{label}</span>
                <span className={`font-mono text-${color}-300`}>{fmt(val || 0)}</span>
              </div>
            ))}
          </div>
          <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-2 text-[11px]">
            <div className="text-zinc-500 mb-1">Payment History</div>
            <div className="text-zinc-300">
              {client.history?.receipts ? `${client.history.receipts} receipts received (₹${(client.history.total_received || 0).toLocaleString('en-IN')} total)` : 'No payments recorded yet.'}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, val }) {
  return (
    <div className="bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5">
      <div className="text-[9px] uppercase text-zinc-500">{label}</div>
      <div className="text-zinc-100 font-mono font-semibold text-sm">{val}</div>
    </div>
  );
}
